/**
 * validation.ts — pure investment-criteria validation.
 *
 * There was no pure criteria validation in the web-app (investorOnboarding
 * relies on slider min/max attributes in the DOM), so this module is new —
 * written to the contract: every check is pure and each returns a
 * human-readable message. Order of messages is stable (field order).
 */

import type { InvestmentCriteria, InvestmentStrategy, InvestorFinancingType } from './types.js';

const FINANCING_TYPES: InvestorFinancingType[] = ['dscr', 'conventional', 'hard_money', 'cash'];

const STRATEGIES: InvestmentStrategy[] = [
  'buy_and_hold',
  'flip',
  'brrrr',
  'wholesale',
  'short_term_rental',
];

function isFiniteNumber(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

function inRange(v: number, min: number, max: number): boolean {
  return v >= min && v <= max;
}

/**
 * Validate a partial InvestmentCriteria. Returns a list of error messages;
 * empty means valid. Unset (undefined) fields are skipped — criteria are
 * optional by design.
 */
export function validateInvestmentCriteria(c: Partial<InvestmentCriteria>): string[] {
  const errors: string[] = [];

  const { min_price, max_price } = c;
  if (min_price !== undefined && (!isFiniteNumber(min_price) || min_price < 0)) {
    errors.push('min_price must be a non-negative number');
  }
  if (max_price !== undefined && (!isFiniteNumber(max_price) || max_price < 0)) {
    errors.push('max_price must be a non-negative number');
  }
  if (isFiniteNumber(min_price) && isFiniteNumber(max_price) && min_price >= max_price) {
    errors.push('min_price must be less than max_price');
  }

  const rateFields: { key: 'min_cap_rate' | 'min_cash_on_cash'; label: string }[] = [
    { key: 'min_cap_rate', label: 'min_cap_rate' },
    { key: 'min_cash_on_cash', label: 'min_cash_on_cash' },
  ];
  for (const { key, label } of rateFields) {
    const v = c[key];
    if (v !== undefined && (!isFiniteNumber(v) || !inRange(v, 0, 100))) {
      errors.push(`${label} must be between 0 and 100`);
    }
  }

  const { min_dscr } = c;
  if (min_dscr !== undefined && (!isFiniteNumber(min_dscr) || min_dscr < 0)) {
    errors.push('min_dscr must be a non-negative number');
  }

  const { max_rehab_budget } = c;
  if (max_rehab_budget !== undefined && (!isFiniteNumber(max_rehab_budget) || max_rehab_budget < 0)) {
    errors.push('max_rehab_budget must be a non-negative number');
  }

  const { financing_type } = c;
  if (financing_type !== undefined && !FINANCING_TYPES.includes(financing_type)) {
    errors.push(`financing_type must be one of: ${FINANCING_TYPES.join(', ')}`);
  }

  const { down_payment_pct } = c;
  if (down_payment_pct !== undefined && (!isFiniteNumber(down_payment_pct) || !inRange(down_payment_pct, 0, 100))) {
    errors.push('down_payment_pct must be between 0 and 100');
  }

  const { interest_rate } = c;
  if (interest_rate !== undefined && (!isFiniteNumber(interest_rate) || !inRange(interest_rate, 0, 25))) {
    errors.push('interest_rate must be between 0 and 25');
  }

  const { loan_term_years } = c;
  if (loan_term_years !== undefined && (!isFiniteNumber(loan_term_years) || !inRange(loan_term_years, 1, 40))) {
    errors.push('loan_term_years must be between 1 and 40');
  }

  const { portfolio_size } = c;
  if (portfolio_size !== undefined && (!isFiniteNumber(portfolio_size) || portfolio_size < 0 || !Number.isInteger(portfolio_size))) {
    errors.push('portfolio_size must be a non-negative integer');
  }

  const { strategy } = c;
  if (strategy !== undefined && !STRATEGIES.includes(strategy)) {
    errors.push(`strategy must be one of: ${STRATEGIES.join(', ')}`);
  }

  return errors;
}
